import React from 'react';
import PropTypes from 'prop-types';
import { Typography, QuantitySelector } from '@telus-uds/components-web';
import {
  FieldRowOuterWrapper,
  FieldRowWrapper,
  StyledLabelWrapper,
  PlusMinusWrapper,
  PlusMinusDropdownBlock,
  PlusMinusDropdownSelect,
  InputTitle,
  BoldFieldLabel
} from './css';
import { useLocaleContext } from '../../context/LocaleContext';
import { localeFr } from '../../misc';
import Note from '../../note';

const FieldPlusMinusInput = ({ data }) => {
  const {
    ref,
    name,
    label,
    title,
    value,
    minValue,
    maxValue,
    onChange,
    hideButtons,
    formFields,
    isHidden,
    onValueOverLimit,
    note,
    noteAppearsBelowField,
    isShowNoteBackground,
    isItalicNote,
    hasDropdown,
    dropdownOptions,
    dropdownValue
  } = data;

  const locale = useLocaleContext();
  const dropdownName = `${name}_dropdown`;

  const labelLogic =
    formFields && formFields.secureBusinessComments
      ? label.split('($')[0]
      : label;

  // Store only the value key (e.g. oneTime, 3yEasyPay); label may show price for display.
  const getDropdownOptionValue = opt => {
    if (opt.fields && opt.fields.value !== undefined) return opt.fields.value;
    return opt.value;
  };

  const getDropdownOptionLabel = opt => {
    const fields = opt || {};
    const dropDownlabel = locale === localeFr ? fields.labelFr : fields.labelEn;
    return dropDownlabel;
  };

  const handleDropdownChange = e => {
    const selectedValue = e.target.value;
    onChange({ target: { name: dropdownName, value: selectedValue } });
  };

  if (isHidden) return null;

  const numericValue = (() => {
    const hasValue = !(value === '' || value === null || typeof value === 'undefined');
    const parsed = hasValue ? Number(value) : NaN;

    if (!Number.isNaN(parsed)) {
      return parsed;
    }

    if (typeof minValue === 'number') {
      return minValue;
    }

    return 0;
  })();

  const handleQuantityChange = newNumber => {
    if (typeof onChange !== 'function' || typeof newNumber !== 'number') {
      return;
    }
    let clamped = newNumber;
    if (clamped < (minValue || 0)) {
      clamped = minValue || 0;
    }
    if (typeof maxValue === 'number' && clamped > maxValue) {
      clamped = maxValue;
      if (onValueOverLimit) onValueOverLimit(maxValue);
    }
    onChange({
      target: {
        value: clamped.toString(),
        name
      },
      preventDefault: () => {}
    });
  };

  return (
    <>
      {title && (
        <InputTitle>
          <Typography>{title}</Typography>
        </InputTitle>
      )}
      <FieldRowOuterWrapper>
        <FieldRowWrapper>
          {label && (
            <StyledLabelWrapper>
              <BoldFieldLabel><Typography block heading="h4" variant={{ size: 'h4', bold: true }}>{labelLogic}</Typography></BoldFieldLabel>
              {note && note.content && !noteAppearsBelowField && (
                <div style={{ marginTop: '10px' }}>
                  <Note
                    type="richText"
                    data={note}
                    isShowNoteBackground={isShowNoteBackground}
                    isItalicNote={isItalicNote}
                  />
                </div>
              )}
            </StyledLabelWrapper>
          )}
          <PlusMinusWrapper>
            <QuantitySelector
              ref={ref}
              id={name}
              value={numericValue}
              minNumber={typeof minValue === 'number' ? minValue : undefined}
              maxNumber={typeof maxValue === 'number' ? maxValue : undefined}
              onChange={handleQuantityChange}
              inactive={hideButtons}
              variant={{ alternative: false, disabled: hideButtons }}
            />
          </PlusMinusWrapper>
        </FieldRowWrapper>
        {hasDropdown && (
          <PlusMinusDropdownBlock>
            <PlusMinusDropdownSelect
              id={dropdownName}
              name={dropdownName}
              value={dropdownValue != null ? dropdownValue : ''}
              onChange={handleDropdownChange}
            >
              <option value="" />
              {dropdownOptions &&
                dropdownOptions.map((opt, index) => {
                  const optValue = getDropdownOptionValue(opt);
                  const optLabel = getDropdownOptionLabel(opt);
                  if (optValue === undefined) return null;
                  return (
                    <option key={index} value={optValue}>
                      {optLabel != null ? optLabel : optValue}
                    </option>
                  );
                })}
            </PlusMinusDropdownSelect>
          </PlusMinusDropdownBlock>
        )}
      </FieldRowOuterWrapper>
      {note && note.content && noteAppearsBelowField && (
        <Note
          type="richText"
          data={note}
          isShowNoteBackground={isShowNoteBackground}
        />
      )}
    </>
  );
};

FieldPlusMinusInput.propTypes = {
  data: PropTypes.shape({
    ref: PropTypes.object, // eslint-disable-line
    label: PropTypes.string,
    title: PropTypes.string,
    minValue: PropTypes.number,
    maxValue: PropTypes.number,
    hideButtons: PropTypes.bool,
    isHidden: PropTypes.bool,
    value: PropTypes.string,
    name: PropTypes.string.isRequired,
    formFields: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    note: PropTypes.shape({
      content: PropTypes.arrayOf(PropTypes.object)
    }),
    noteAppearsBelowField: PropTypes.bool,
    isShowNoteBackground: PropTypes.bool,
    isItalicNote: PropTypes.bool,
    hasDropdown: PropTypes.bool,
    dropdownOptions: PropTypes.arrayOf(PropTypes.object),
    dropdownValue: PropTypes.string
  }).isRequired
};

export default FieldPlusMinusInput;
